// <pfs-loading-indicator>: Loading-Anzeige fuer den Main-Bereich
// (oder kleiner inline) mit Spinner + Custom-Text + animierten
// Dots (1 → 2 → 3 → 1 Loop, 1.5s).
//
// Use-cases:
//   1. HTMX-Swap-Indikator: <pfs-loading-indicator hx-indicator>
//      mit `htmx-indicator`-CSS-Klasse vom Caller — heute angenommen
//      dass der Caller die hx-indicator-Konvention nutzt.
//   2. Initial-Render-State: WC mit pending data zeigt selbst ein
//      `<pfs-loading-indicator>` bis state.loading auf false geht.
//   3. Standalone: ueberall wo eine "Lade…"-Animation sichtbar sein
//      soll.
//
// Beispiel:
//   <pfs-loading-indicator
//     data-text="Termine werden geladen"
//     data-variant="main"></pfs-loading-indicator>
//
// Attribute (alle data-*):
//   data-text     required — Lade-Text. Server-rendered i18n vom
//                 Caller (kein WC-internes Wording).
//   data-variant  "main" | "inline" (default "main"). main = grosser
//                 zentrierter Spinner mit Text drunter. inline =
//                 kleiner Spinner mit Text rechts.
//
// Kein JS-Timer fuer die Dots — pure CSS-Animation.

import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { SHADOW_RESET_CSS } from "../../styles/shadow-reset.js";

const STYLES = `
${SHADOW_RESET_CSS}
:host {
  /* main-Variant: SELBST-zentrierend. Der Host IST der flex-center-Container +
     trägt eine intrinsische min-height — so sitzt der Spinner auch in einem
     bare min-height-Block-Container mittig, nicht nur wenn der Consumer den
     Container zufällig selbst flex-zentriert (früher: height 100% kollabierte in
     einem min-height-Block → Spinner klebte oben). height 100% füllt zusätzlich,
     wenn der Container eine echte Höhe hat. inline überschreibt alles. */
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: var(--pfs-loading-min-height, 12rem);
  height: 100%;
}

:host([data-variant="inline"]) {
  height: auto;
  width: auto;
  min-height: 0;
  display: inline-block;
}

:host([data-variant="inline"]) .wrapper {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0;
  height: auto;
}

:host([data-variant="inline"]) .spinner {
  width: 1rem;
  height: 1rem;
  border-width: 2px;
}

:host([data-variant="inline"]) .text {
  font-size: 0.875rem;
}

/* Default = main: volle Container-Hoehe + zentriert */
.wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  padding: 1rem;
  color: var(--pfs-color-on-surface-muted);
  height: 100%;
  width: 100%;
  box-sizing: border-box;
}

.spinner {
  width: 2.5rem;
  height: 2.5rem;
  border: 3px solid var(--pfs-color-outline);
  border-top-color: var(--pfs-color-primary);
  border-radius: 50%;
  animation: pfs-spin 0.9s linear infinite;
}

@keyframes pfs-spin {
  to { transform: rotate(360deg); }
}

.text {
  font-size: 1rem;
  font-weight: 500;
  display: inline-flex;
  align-items: baseline;
}

/* Animierte Dots: ::after rendert das Trio "...", animation steppt
   zwischen 1, 2, 3 sichtbaren Zeichen via clip-path. So bleibt die
   Layout-Breite konstant (... immer reserviert). */
.dots {
  display: inline-block;
  width: 1.2em;
  text-align: left;
  font-variant-numeric: tabular-nums;
}
.dots::after {
  content: "...";
  animation: pfs-dots 1.5s steps(1, end) infinite;
  clip-path: inset(0 100% 0 0);
}
@keyframes pfs-dots {
  0%   { clip-path: inset(0 66% 0 0); }   /* "."   */
  33%  { clip-path: inset(0 33% 0 0); }   /* ".."  */
  66%  { clip-path: inset(0 0 0 0); }     /* "..." */
}
`;

defineComponent({
  tagName: "pfs-loading-indicator",
  mixins: [withShadow()],
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;
    const wrapper = document.createElement("div");
    wrapper.className = "wrapper";
    wrapper.setAttribute("role", "status");
    wrapper.setAttribute("aria-live", "polite");
    const spinner = document.createElement("div");
    spinner.className = "spinner";
    spinner.setAttribute("aria-hidden", "true");
    const textEl = document.createElement("div");
    textEl.className = "text";
    const label = document.createElement("span");
    const dots = document.createElement("span");
    dots.className = "dots";
    dots.setAttribute("aria-hidden", "true");
    textEl.append(label, dots);
    wrapper.append(spinner, textEl);
    root.append(style, wrapper);

    // data-text reaktiv (data-variant treibt rein CSS via :host([data-variant])).
    const paint = (): void => {
      label.textContent = host.dataset.text ?? "";
    };
    paint();
    const obs = new MutationObserver(paint);
    ctx.cleanup.push(() => obs.disconnect());
    obs.observe(host, { attributes: true, attributeFilter: ["data-text"] });
  },
});
